import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useImmer } from "use-immer";
import cn from "classnames";

import {
    ButtonToolbar,
    Container,
    Content,
    FlexboxGrid,
    IconButton,
    List,
    VStack,
    Text,
} from "rsuite";

import ArrowRightLineIcon from "@rsuite/icons/ArrowRightLine";
import ArrowLeftLineIcon from "@rsuite/icons/ArrowLeftLine";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";

import { TtpMessageSourceField } from "store/environments/tenants/custom-fields/erp-types";

import Overflow from "components/Common/Overflow/Overflow";

import "./SourceFieldPicker.less";

enum Direction {
    Left = 0,
    Right = 1,
    Up = 2,
    Down = 3,
}

export type ListItem<T> = T & {
    selected?: boolean;
};

interface Props {
    data: ReadonlyArray<TtpMessageSourceField>;
    value: ReadonlyArray<TtpMessageSourceField>;

    onChange?: (value: TtpMessageSourceField[]) => void;
}

const SourceFieldPicker: FC<Props> = ({ data, value, onChange }: Props) => {
    const [available, setAvailable] = useImmer<
        ListItem<TtpMessageSourceField>[]
    >([
        ...data
            .filter((item) => !value.some((v) => v.field === item.field))
            .map((item) => ({
                ...item,
                selected: false,
            })),
    ]);

    const [selected, setSelected] = useImmer<ListItem<TtpMessageSourceField>[]>(
        [
            ...data
                .filter((item) => value.some((v) => v.field === item.field))
                .map((item) => ({
                    ...item,
                    selected: false,
                })),
        ]
    );

    const valueEqualSelected = useCallback(
        (value: ReadonlyArray<TtpMessageSourceField>) => {
            if (value.length !== selected.length) {
                return false;
            }

            return value.every((v, i) => v.field === selected[i].field);
        },
        [selected]
    );

    const moveLeft = useMemo(
        () => selected.some((item) => item.selected),
        [selected]
    );

    const moveRight = useMemo(
        () => available.some((item) => item.selected),
        [available]
    );

    const moveUp = useMemo(
        () =>
            selected.some((item) => item.selected) &&
            selected
                .filter((item) => item.selected)
                .every((item) => selected.indexOf(item) > 0),
        [selected]
    );

    const moveDown = useMemo(
        () =>
            selected.some((item) => item.selected) &&
            selected
                .filter((item) => item.selected)
                .every((item) => selected.indexOf(item) < selected.length - 1),
        [selected]
    );

    const handleLeftSelect = (index: number) => {
        setAvailable((draft) => {
            draft[index].selected = !draft[index].selected;
        });

        setSelected((draft) => {
            draft.forEach((item) => {
                item.selected = false;
            });
        });
    };

    const handleRightSelect = (index: number) => {
        setSelected((draft) => {
            draft[index].selected = !draft[index].selected;
        });

        setAvailable((draft) => {
            draft.forEach((item) => {
                item.selected = false;
            });
        });
    };

    const handleMove = (direction: Direction) => {
        switch (direction) {
            case Direction.Left:
                const toMoveLeft = selected.filter((item) => item.selected);

                setAvailable((draft) => {
                    draft.unshift(...toMoveLeft.map((item) => ({ ...item })));

                    draft.forEach((item) => {
                        item.selected = false;
                        item.line = 0;
                    });
                });

                setSelected((draft) => {
                    return draft.filter((item) => !item.selected);
                });

                break;
            case Direction.Right:
                const toMoveRight = available.filter((item) => item.selected);

                setSelected((draft) => {
                    draft.push(...toMoveRight.map((item) => ({ ...item })));

                    draft.forEach((item, index) => {
                        item.selected = false;
                        item.line = index;
                    });
                });

                setAvailable((draft) => {
                    return draft.filter((item) => !item.selected);
                });

                break;
            case Direction.Up:
                setSelected((draft) => {
                    draft
                        .map((item, i) => (item.selected ? i : -1))
                        .filter((i) => i > 0)
                        .forEach((i) => {
                            if (!draft[i - 1].selected) {
                                [draft[i - 1], draft[i]] = [
                                    draft[i],
                                    draft[i - 1],
                                ];
                            }
                        });

                    draft.forEach((item, index) => {
                        item.line = index;
                    });
                });

                break;
            case Direction.Down:
                setSelected((draft) => {
                    draft
                        .map((item, i) => (item.selected ? i : -1))
                        .filter((i) => i >= 0 && i < draft.length - 1)
                        .reverse()
                        .forEach((i) => {
                            [draft[i], draft[i + 1]] = [draft[i + 1], draft[i]];
                        });

                    draft.forEach((item, index) => {
                        item.line = index;
                    });
                });

                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (valueEqualSelected(value)) {
            return;
        }

        setSelected(
            value.map((item) => ({
                ...item,
                selected: false,
            }))
        );

        setAvailable(
            data
                .filter((item) => !value.some((v) => v.field === item.field))
                .map((item) => ({
                    ...item,
                    line: 0,
                    selected: false,
                }))
        );
    }, [value]);

    useEffect(() => {
        if (valueEqualSelected(value)) {
            return;
        }

        onChange?.(selected);
    }, [selected]);

    return (
        <Container className="source-field-picker">
            <Content className="source-field-picker__content">
                <FlexboxGrid
                    className="source-field-picker__grid"
                    align="middle"
                    justify={"space-around"}
                >
                    <FlexboxGrid.Item className="source-field-picker__list-container">
                        <List
                            className="source-field-picker__list"
                            size={"sm"}
                            hover
                        >
                            {available.map((item, index) => (
                                <List.Item
                                    key={index}
                                    index={index}
                                    onClick={() => handleLeftSelect(index)}
                                    className={cn(
                                        "source-field-picker__list-item",
                                        { selected: item.selected }
                                    )}
                                >
                                    <Text size={"md"}>
                                        <Overflow placement={"left"}>
                                            {item.field}
                                        </Overflow>
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item>
                        <ButtonToolbar>
                            <VStack>
                                <IconButton
                                    disabled={!moveRight}
                                    icon={<ArrowRightLineIcon />}
                                    onClick={() => {
                                        handleMove(Direction.Right);
                                    }}
                                    className={cn(
                                        "source-field-picker__control",
                                        { enabled: moveRight }
                                    )}
                                />

                                <IconButton
                                    disabled={!moveLeft}
                                    icon={<ArrowLeftLineIcon />}
                                    onClick={() => {
                                        handleMove(Direction.Left);
                                    }}
                                    className={cn(
                                        "source-field-picker__control",
                                        { enabled: moveLeft }
                                    )}
                                />
                            </VStack>
                        </ButtonToolbar>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item className="source-field-picker__list-container">
                        <List
                            className="source-field-picker__list"
                            size={"sm"}
                            hover
                        >
                            {selected.map((item, index) => (
                                <List.Item
                                    key={index}
                                    index={index}
                                    onClick={() => handleRightSelect(index)}
                                    className={cn(
                                        "source-field-picker__list-item",
                                        { selected: item.selected }
                                    )}
                                >
                                    <Text size={"md"}>
                                        <Overflow placement={"right"}>
                                            {item.field}
                                        </Overflow>
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item>
                        <ButtonToolbar>
                            <VStack>
                                <IconButton
                                    disabled={!moveUp}
                                    icon={<ArrowUpLineIcon />}
                                    onClick={() => {
                                        handleMove(Direction.Up);
                                    }}
                                    className={cn(
                                        "source-field-picker__control",
                                        { enabled: moveUp }
                                    )}
                                />

                                <IconButton
                                    disabled={!moveDown}
                                    icon={<ArrowDownLineIcon />}
                                    onClick={() => {
                                        handleMove(Direction.Down);
                                    }}
                                    className={cn(
                                        "source-field-picker__control",
                                        { enabled: moveDown }
                                    )}
                                />
                            </VStack>
                        </ButtonToolbar>
                    </FlexboxGrid.Item>
                </FlexboxGrid>
            </Content>
        </Container>
    );
};

export default SourceFieldPicker;
